import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsFieldHeightMixin from '../../mixins/ids-field-height-mixin/ids-field-height-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import { stripHTML } from '../../utils/ids-xss-utils/ids-xss-utils';
import IdsInput from '../ids-input/ids-input';
import IdsMenuButton from '../ids-menu-button/ids-menu-button';
import IdsMenuGroup from '../ids-menu/ids-menu-group';
import IdsPopupMenu from '../ids-popup-menu/ids-popup-menu';
import styles from './ids-filter-field.scss';

export enum ReadonlyValue {
  BUTTON = 'button',
  INPUT = 'input',
  ALL = ''
}

const Base = IdsLabelPositionMixin(
  IdsFieldHeightMixin(
    IdsEventsMixin(IdsElement)
  )
);

export interface FilterFieldOperator {
  value: string;
  text: string;
  icon: string;
  selected?: boolean;
}

export const FilterFieldTypes = ['text', 'date', 'time', 'dropdown'];

/**
 * IDS Filter Field Component
 * @type {IdsFilterField}
 * @mixes IdsEventsMixin
 * @mixes IdsFieldHeightMixin
 * @mixes IdsLabelPositionMixin
 */
@customElement('ids-filter-field')
@scss(styles)
export default class IdsFilterField extends Base {
  #operators: FilterFieldOperator[] = [];

  /** Cache last input change event data for next change event */
  #lastInputChange: Record<string, any> = {};

  /** Flag to prevent change event from triggering */
  #suppressChangeEvent = false;

  /** Store original field-height when compact is activated */
  #storedFieldHeight: string | null = null;

  constructor() {
    super();
  }

  static get attributes(): string[] {
    return [
      ...super.attributes,
      attributes.LABEL,
      attributes.OPERATOR,
      attributes.READONLY,
      attributes.SIZE,
      attributes.VALUE
    ];
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.#attachEventHandlers();

    // Apply readonly state to button if set via HTML attribute
    const readonlyVal = this.getAttribute(attributes.READONLY);
    if (readonlyVal !== null) {
      const buttonReadonly = readonlyVal === '' || readonlyVal === 'true' || readonlyVal === 'button';
      if (buttonReadonly && this.menuButton) {
        this.menuButton.disabled = true;
      } else if (this.menuButton) {
        this.menuButton.disabled = false;
      }
    }
  }

  template() {
    return `<div class="ids-filter-field">
      <div class="field-container">
        <ids-menu-button id="operator-button" part="menu-button" menu="operator-menu" icon="filter-equals" dropdown-icon>
          <span class="audible">Icon only button</span>
        </ids-menu-button>
        <ids-popup-menu id="operator-menu" target="opereator-button" trigger-type="click">
          <ids-menu-group select="single">
            <ids-menu-item value="equals" icon="filter-equals" selected="true">Equals</ids-menu-item>
            <ids-menu-item value="in-range" icon="filter-in-range">In Range</ids-menu-item>
            <ids-menu-item value="does-not-equal" icon="filter-does-not-equal">Does Not Equal</ids-menu-item>
            <ids-menu-item value="before" icon="filter-less-than">Before</ids-menu-item>
            <ids-menu-item value="on-or-before" icon="filter-less-equals">On Or Before</ids-menu-item>
            <ids-menu-item value="after" icon="filter-greater-than">After</ids-menu-item>
            <ids-menu-item value="on-or-after" icon="filter-greater-equals">On Or After</ids-menu-item>
          </ids-menu-group>
        </ids-popup-menu>
        <slot id="input-slot"></slot>
      </div>
    </div>`;
  }

  #attachEventHandlers() {
    const inputSlot = this.container?.querySelector<HTMLSlotElement>('#input-slot');
    this.offEvent('slotchange', inputSlot);
    this.onEvent('slotchange', inputSlot, () => {
      this.#configureInputElement(this.inputElement);
      this.#attachInputChangeHandler(this.inputElement);
    });

    this.offEvent('selected.operators', this.menu);
    this.onEvent('selected.operators', this.menu, (evt: CustomEvent) => {
      evt.stopPropagation();
      this.#updateOperatorIcon(evt.detail.elem.icon);
      this.triggerChangeEvent();
    });
  }

  #attachInputChangeHandler(inputElement?: Element) {
    this.onEvent('change.input', inputElement, (evt: CustomEvent) => {
      evt.stopPropagation();
      this.#lastInputChange = { ...evt.detail };
      this.triggerChangeEvent();
    });
  }

  #updateOperatorIcon(icon: string) {
    this.menuButton?.setAttribute(attributes.ICON, icon);
  }

  /** Trigger the change event */
  triggerChangeEvent() {
    if (this.#suppressChangeEvent) return;

    this.triggerEvent('change', this, {
      detail: {
        operator: this.operator,
        ...this.#lastInputChange,
        value: this.value,
        elem: this.inputElement
      }
    });
  }

  /**
   * Push field-height/compact to the container element
   * @param {string} val the new field height setting
   */
  onFieldHeightChange(val: string) {
    if (val) {
      const isCompact = val === attributes.COMPACT;
      if (isCompact) {
        // Store current field-height before going compact
        this.#storedFieldHeight = this.menuButton?.getAttribute(attributes.FIELD_HEIGHT) || null;
      }

      const menuBtnAttr = { name: attributes.FIELD_HEIGHT, val: isCompact ? 'xs' : val };
      const inputAttr = { name: isCompact ? attributes.COMPACT : attributes.FIELD_HEIGHT, val: isCompact ? '' : val };

      this.menuButton?.setAttribute(menuBtnAttr.name, menuBtnAttr.val);
      this.slottedField?.setAttribute(inputAttr.name, inputAttr.val);
    } else {
      // Restore stored field-height if available, otherwise set to default
      const restoreHeight = this.#storedFieldHeight || 'md';
      this.menuButton?.setAttribute(attributes.FIELD_HEIGHT, restoreHeight);
      this.slottedField?.setAttribute(attributes.FIELD_HEIGHT, restoreHeight);
      this.menuButton?.removeAttribute(attributes.COMPACT);
      this.slottedField?.removeAttribute(attributes.COMPACT);
    }
  }

  /**
   * Syncs slotted input element labels/id with filter field
   * @param {string} label label string
   */
  #syncInputLabel(label: string | null) {
    const inputElement = this.inputElement;
    const safeLabel = stripHTML(label ?? '');

    if (inputElement && safeLabel) {
      this.setAttribute(attributes.LABEL, safeLabel);
      this.inputElement?.setAttribute(attributes.LABEL, safeLabel);
    } else {
      this.removeAttribute(attributes.LABEL);
      this.inputElement?.removeAttribute(attributes.LABEL);
    }
  }

  /**
   * Initial configuration of slotted input element
   * @param {Element} inputElement input element
   */
  #configureInputElement(inputElement?: Element) {
    if (!inputElement) return;
    const inputLabel = this.inputElementLabel;
    const operatorBtn = this.menuButton;

    inputLabel?.style.setProperty('inset-inline-start', `-${operatorBtn?.clientWidth || 52}px`);
    inputLabel?.style.setProperty('position', 'relative');
    inputElement.toggleAttribute(attributes.NO_MARGINS, true);
    inputElement.setAttribute(attributes.SIZE, this.size);
    this.#syncInputLabel((inputElement as IdsInput).label || this.label);

    const inputSR = inputElement.shadowRoot;
    const inputField = inputSR?.querySelector('.field-container')
                    || inputSR?.querySelector('ids-trigger-field')?.shadowRoot?.querySelector('.field-container');
    inputField?.classList.add('ff-input-field');

    // Sync field-height/compact from parent to both menu button and input
    if (this.compact) {
      this.menuButton?.setAttribute(attributes.FIELD_HEIGHT, 'xs');
      inputElement.setAttribute(attributes.COMPACT, '');
    } else if (this.fieldHeight && this.fieldHeight !== 'md') {
      this.menuButton?.setAttribute(attributes.FIELD_HEIGHT, this.fieldHeight);
      inputElement.setAttribute(attributes.FIELD_HEIGHT, this.fieldHeight);
    }

    // Sync readonly state to newly slotted input
    const readonlyVal = this.getAttribute(attributes.READONLY);
    if (readonlyVal !== null) {
      const inputReadonly = readonlyVal === '' || readonlyVal === 'true' || readonlyVal === 'input';
      inputElement.toggleAttribute(attributes.READONLY, inputReadonly);
    }
  }

  /**
   * Renders operators configuration in menu element
   * @param {Array<FilterFieldOperator>} operators operators config
   */
  #renderOperators(operators: FilterFieldOperator[]) {
    if (!operators.length || !this.isConnected) return;

    const menuGroup = this.container?.querySelector<IdsMenuGroup>('ids-menu-group');
    if (menuGroup) {
      menuGroup.innerHTML = this.#operatorsTemplate(operators);
      menuGroup.refresh();
    }
  }

  #operatorsTemplate(operators: FilterFieldOperator[]): string {
    return operators.map((operator) => {
      const value = `value="${operator.value}"`;
      const icon = `icon="${operator.icon}"`;
      const selected = operator.selected ? 'selected' : '';
      const menuItem = `<ids-menu-item ${value} ${icon} ${selected}>${operator.text}</ids-menu-item>`;

      return menuItem;
    }).join('');
  }

  /**
   * Get the slotted input element
   * @returns {IdsInput | null} the input element
   */
  get inputElement() {
    return this.container?.querySelector<HTMLSlotElement>('#input-slot')?.assignedElements()[0] as IdsInput || null;
  }

  /**
   * Get the input element label
   * @returns {HTMLLabelElement | null | undefined} the label element
   */
  get inputElementLabel() {
    const inputElement = this.inputElement;

    if (inputElement.tagName === 'IDS-INPUT') {
      return inputElement.shadowRoot?.querySelector<HTMLLabelElement>('label');
    }

    const inputTriggerField = inputElement.shadowRoot?.querySelector('ids-trigger-field');
    return inputTriggerField?.shadowRoot?.querySelector<HTMLLabelElement>('label');
  }

  /**
   * Get the operators menu button element
   * @returns {IdsMenuButton | null | undefined} the operator menu button
   */
  get menuButton() {
    return this.container?.querySelector<IdsMenuButton>('ids-menu-button');
  }

  /**
   * Get the operator menu
   * @returns {IdsPopupMenu | null | undefined} the operator menu
   */
  get menu() {
    return this.container?.querySelector<IdsPopupMenu>('ids-popup-menu');
  }

  /**
   * Sets input element value
   * @param {string | null } val input value
   */
  set value(val: string | null) {
    if (this.inputElement) this.inputElement.value = val;
  }

  /**
   * Get the input element value
   * @returns {string} the input element value
   */
  get value() {
    return this.inputElement?.value || this.inputElement?.getAttribute(attributes.VALUE);
  }

  /**
   * Sets operator
   * @param {string} op operator id
   */
  set operator(op: string) {
    this.setAttribute(attributes.OPERATOR, op);
    this.menuButton?.setAttribute(attributes.VALUE, op);
  }

  /**
   * Get the selected operator
   * @returns {string} the operator id
   */
  get operator(): string {
    return Array.isArray(this.menuButton?.value) ? this.menuButton?.value[0] : this.menuButton?.value;
  }

  /**
   * Sets operators config for menu button
   * @param {Array<FilterFieldOperator>} val operators configuration
   */
  set operators(val: FilterFieldOperator[]) {
    this.#suppressChangeEvent = true;
    if (val?.length) {
      this.#operators = val;
      this.#renderOperators(this.#operators);
      const selectedOperator = val.find((op) => op.selected)?.value || val[0].value;
      this.menuButton?.setAttribute(attributes.VALUE, selectedOperator);
    }
    this.#suppressChangeEvent = false;
  }

  /**
   * Get the operators config
   * @returns {Array<FilterFieldOperator>} the operators configuration
   */
  get operators(): FilterFieldOperator[] {
    return this.#operators;
  }

  /**
   * Sets label of input component
   * @param {string|null} val label string
   */
  set label(val: string | null) {
    const safeLabel = stripHTML(val ?? '');

    if (safeLabel) {
      this.setAttribute(attributes.LABEL, safeLabel);
    } else {
      this.removeAttribute(attributes.LABEL);
    }

    this.#syncInputLabel(safeLabel);
  }

  /**
   * Get the label of input component
   * @returns {string | null} the label string
   */
  get label() {
    return this.getAttribute(attributes.LABEL);
  }

  /**
   * Sets size
   * @param {string} val size
   */
  set size(val: string) {
    this.setAttribute(attributes.SIZE, val);
    this.inputElement?.setAttribute(attributes.SIZE, val);
  }

  /**
   * Get the size
   * @returns {string} the size (default 'md')
   */
  get size(): string {
    return this.getAttribute(attributes.SIZE) ?? 'md';
  }

  /**
   * Get the slotted field
   * @returns {HTMLElement} the slotted field
   */
  get slottedField() {
    return this.shadowRoot?.querySelector('slot')?.assignedElements()[0] as HTMLElement;
  }

  /**
   * Sets the readonly state.
   * @param {string | boolean | null} val readonly mode
   */
  set readonly(val: string | boolean | null) {
    if (![...Object.values(ReadonlyValue), '', 'true', true, false, null].includes(val)) {
      this.removeAttribute(attributes.READONLY);
      return;
    }

    if (['false', false, null].includes(val)) {
      this.removeAttribute(attributes.READONLY);
      this.#applyReadonly(null);
      return;
    }

    if (val === true) {
      this.toggleAttribute(attributes.READONLY, true);
      this.#applyReadonly(true);
      return;
    }

    this.setAttribute(attributes.READONLY, val as string);
    this.#applyReadonly(val);
  }

  /**
   * Gets the readonly attribute value.
   * @returns {string | null} '', 'input', 'button', or null
   */
  get readonly(): string | null {
    return this.getAttribute(attributes.READONLY);
  }

  /**
   * Update menu button and input fields based on filter field readonly value
   * @param { boolean | string | null } readonlyValue accepts readonly value scenario
   */
  #applyReadonly(readonlyValue: boolean | string | null): void {
    const input = this.inputElement;
    const menuBtn = this.menuButton;

    if (!input || !menuBtn) {
      return;
    }

    if (readonlyValue === null) {
      input.removeAttribute(attributes.READONLY);
      menuBtn.removeAttribute(attributes.DISABLED);
      return;
    }

    if (['true', '', true].includes(readonlyValue)) {
      input.setAttribute(attributes.READONLY, '');
      menuBtn.setAttribute(attributes.DISABLED, 'true');
      return;
    }

    if (readonlyValue === 'input') {
      menuBtn.removeAttribute(attributes.DISABLED);
      input.setAttribute(attributes.READONLY, '');
      return;
    }

    if (readonlyValue === 'button') {
      menuBtn.setAttribute(attributes.DISABLED, 'true');
      input.removeAttribute(attributes.READONLY);
    }
  }

  /**
   * Push label-position to the trigger-field element
   * @returns {void}
   */
  onLabelPositionChange(): void {
    this.inputElement?.setAttribute('label-position', this.labelPosition ?? '');
  }

  /**
   * Push label-width to the trigger-field element
   * @returns {void}
   */
  onLabelWidthChange(): void {
    this.inputElement?.setAttribute('label-width', this.labelWidth ?? '');
  }

  /**
   * Push label-state to the trigger-field element
   * @returns {void}
   */
  onLabelAlignYChange(): void {
    this.inputElement?.setAttribute('label-align-y', this.labelAlignY ?? '');
  }
}
